import React, { useMemo } from "react";
import { IIconProps, mergeStyleSets } from "@fluentui/react";
import { Grid, GridItemChangeEvent, GridRowProps } from "@progress/kendo-react-grid";
import { DragAndDrop, NormalizedDragEvent, useDraggable, useDroppable } from "@progress/kendo-react-common";
import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { ActionButton } from "@fluentui/react/lib/Button";
import { CommandCell, DragHandleCell, KeyValueHeaderCell, KeyValueInputCell } from "./DropDownCells";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { TgkDropDownItem } from "../../../api/models/tgk-dropdown-item";

const addItem: IIconProps = { iconName: "CircleAdditionSolid" };
const editField: string = "inEdit";
type ContextProps = {
  reorder: (dataItem: TgkDropDownItem, direction: "before" | "after" | null) => void;
  dragStart: (dataItem: TgkDropDownItem) => void;
};
export const ReorderContext = React.createContext<ContextProps>({
  reorder: () => {
    return;
  },
  dragStart: () => {
    return;
  },
});

export const updateGridData = (
  gridData: TgkDropDownItem[],
  event: { dataItem: TgkDropDownItem; field: string; value: any }
) => gridData.map((item) => (item.itemId === event.dataItem.itemId ? { ...item, [event.field]: event.value } : item));

interface DropDownGridProps {
  gridData: TgkDropDownItem[];
  setGridData: any;
  isLocked: boolean;
}

const generateId = function generateId(d) {
  return d.reduce((acc, current) => Math.max(acc, current.itemId ?? 0), 0) + 1;
};

export const RowRender = (
  row: React.ReactElement<HTMLTableRowElement, string | React.JSXElementConstructor<any>>,
  rowProps: GridRowProps
) => <DraggableRow elementProps={row.props} {...rowProps} />;

export const DropDownGrid = (props: DropDownGridProps) => {
  const { gridData, setGridData, isLocked } = props;
  const [activeItem, setActiveItem] = React.useState<TgkDropDownItem | null>(null);

  const addNew = () => {
    const newDataItem: TgkDropDownItem = {
      itemId: generateId(gridData),
      inEdit: true,
      key: "",
      value: "",
      isBlurredKey: false,
      isBlurredValue: false,
      keyError: undefined,
      valueError: undefined,
    };
    setGridData(Array.from(new Set([newDataItem, ...gridData])));
  };

  const reorder = (dataItem: TgkDropDownItem, direction: "before" | "after") => {
    if (activeItem === dataItem) {
      return;
    }
    let reorderedData = gridData.slice();
    let prevIndex = reorderedData.findIndex((p) => p === activeItem);
    let nextIndex = reorderedData.findIndex((p) => p === dataItem);
    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(Math.max(nextIndex + (direction === "before" ? -1 : 0), 0), 0, activeItem ?? reorderedData[0]);

    setGridData(reorderedData);
  };

  const itemChange = (event: GridItemChangeEvent) => {
    const newData = gridData.map((item) =>
      item.itemId === event.dataItem.itemId ? { ...item, [event.field || ""]: event.value } : item
    );
    setGridData(newData);
  };

  const deleteItem = (item) => {
    let index = gridData.findIndex((record) => record.itemId === item.itemId);
    gridData.splice(index, 1);
    return gridData;
  };

  const remove = (dataItem: TgkDropDownItem) => {
    if (props.gridData.length > 1) {
      const newData = deleteItem(dataItem);
      setGridData(Array.from(new Set(newData)));
    }
  };

  const reorderContextValue = useMemo(() => ({ reorder, dragStart: setActiveItem }), [reorder, setActiveItem]);

  return (
    <ReorderContext.Provider value={reorderContextValue}>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-col ms-xs12 ms-sm12 ms-md12 ms-lg12">
          <DragAndDrop>
            <Grid
              data={gridData}
              dataItemKey={"itemId"}
              onItemChange={itemChange}
              editField={editField}
              scrollable={"none"}
              rowRender={RowRender}
            >
              <Column title="" cell={DragHandleCell} />
              <Column cell={KeyValueInputCell} headerCell={KeyValueHeaderCell} />
              <Column
                title=""
                cell={CommandCell({ isLocked: isLocked, remove, editField, isRemovable: gridData.length > 2 })}
              />
            </Grid>
          </DragAndDrop>
          <ActionButton
            iconProps={addItem}
            disabled={isLocked}
            onClick={addNew}
            style={{ float: "right", marginBottom: "5px" }}
          >
            <b style={{ color: "var(--color-blue)" }}>{formatMessage("cof.base.additem")}</b>
          </ActionButton>
        </div>
      </div>
    </ReorderContext.Provider>
  );
};

interface DraggableRowProps extends GridRowProps {
  elementProps: any;
}

const DraggableRow = (props: DraggableRowProps) => {
  const [dropped, setDropped] = React.useState(false);
  const [dragged, setDragged] = React.useState(false);
  const [direction, setDirection] = React.useState<"before" | "after" | null>(null);
  const [initial, setInitial] = React.useState({ x: 0, y: 0 });
  const { dragStart, reorder } = React.useContext(ReorderContext);

  const element = React.useRef<HTMLTableRowElement>(null);

  const handlePress = (event: NormalizedDragEvent) => {
    setInitial({ x: event.clientX - event.offsetX, y: event.clientY - event.offsetY });
  };

  const handleDragStart = (event: NormalizedDragEvent) => {
    if (!event.originalEvent.target || !(event.originalEvent.target as HTMLElement).dataset.dragHandle) {
      return;
    }
    setDragged(true);
    dragStart(props.dataItem);
  };

  const handleDrag = (event: NormalizedDragEvent) => {
    if (!element.current || !dragged) {
      return;
    }
    element.current.style.transform = `translateY(${event.clientY - initial.y + event.scrollY}px)`;
  };

  const handleDragEnd = () => {
    setDragged(false);
    setDropped(false);
    setInitial({ x: 0, y: 0 });
  };

  const handleRelease = () => {
    if (!element.current) {
      return;
    }
    element.current.style.transform = null;
  };

  const handleDragEnter = () => {
    setDropped(true);
    setDirection(null);
  };

  const handleDragOver = (event: NormalizedDragEvent) => {
    if (!element.current) {
      return;
    }
    const rect = element.current.getBoundingClientRect();
    setDirection(rect.top + rect.height / 2 <= event.pageY ? "after" : "before");
  };

  const handleDragLeave = () => {
    setDropped(false);
    setDirection(null);
  };

  const handleDrop = () => {
    reorder(props.dataItem, direction);
    setDropped(false);
    setDirection(null);
  };

  useDraggable(
    element,
    {
      onPress: handlePress,
      onDragStart: handleDragStart,
      onDrag: handleDrag,
      onDragEnd: handleDragEnd,
      onRelease: handleRelease,
    },
    { autoScroll: dragged }
  );

  useDroppable(element, {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  });

  const contentStyle = mergeStyleSets({
    placeholderRowInDrag: { outlineStyle: "solid", outlineWidth: 1, outlineColor: "var(--color-blue)" },
    tableRowReset: {
      backgroundColor: "#fff",
      userSelect: "none",
    },
  });

  return (
    <React.Fragment>
      {dropped && direction === "before" && <tr className={contentStyle.placeholderRowInDrag} />}
      <tr
        {...props.elementProps}
        id={props.dataIndex + "row"}
        ref={element}
        className={contentStyle.tableRowReset}
        style={{
          pointerEvents: dragged ? "none" : undefined,
          opacity: dragged ? "0.8" : undefined,
        }}
      />
      <tr
        className={contentStyle.tableRowReset}
        style={{
          height: "12px",
        }}
      />
      {dropped && direction === "after" && <tr className={contentStyle.placeholderRowInDrag} />}
    </React.Fragment>
  );
};
